import {Component, EventEmitter, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ORG_LOGO_CSS, TOAST_TYPE} from "../../constants/Tpiq.constants";
import {SwitchBoard} from "../../services/SwitchBoard";
import {LogoService} from "../../services/LogoService";
import {LoadingService} from "../../services/LoadingService";
import {TaxPlanningService} from "../../tpiq-services/tax-planning.service";
import {ToastService} from "../../services/Toast.service";
import {NumeralService} from "../../services/Numeral.service";
import {Session} from "../../services/Session";
import {environment} from "../../../environments/environment";
import {takeUntil} from 'rxjs/operators';
import {forkJoin} from 'rxjs';
import {FormatAmountPipe} from "../../pipes/format-amount.pipe";
import * as Highcharts from 'highcharts';
import {HighchartsChartModule} from "highcharts-angular";

declare const _: any;

@Component({
  selector: 'app-roi-presentation',
  standalone: true,
  imports: [CommonModule, FormsModule, FormatAmountPipe, HighchartsChartModule],
  templateUrl: './roi-presentation.component.html',
  styleUrl: './roi-presentation.component.scss'
})
export class RoiPresentationComponent implements OnInit, OnDestroy {
  org: string = Session.getOrg();
  user: any = {};
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  currentEnvironment: any;
  @Input() clientId: any;
  @Input() planYear: any;
  @Input() clientData: any;
  @Input() clientTaxplanData: any;
  @Input() readOnly: any;
  planSecondYear: any;
  roiYear1Data: any;
  roiYear2Data: any;
  roiGraphData: any;
  orgName: string;
  orgLogo: string;
  backgroundColor: string;
  logoBg: any;
  chartColors: Array<any> = ['#14B5F0', '#3574e3', '#DFCF6A', '#2D62BF', '#09A4DE', '#475E79', '#3474e3', '#102D79', '#434343'];
  hasROIGraphData = false;
  currentSlide = 'slide-1';
  slides: Array<any> = ['slide-1', 'slide-2', 'slide-3', 'slide-4', 'slide-5', 'slide-6', 'slide-7', 'slide-8', 'slide-9', 'slide-10', 'slide-11', 'slide-12', 'slide-13'];
  disablePrevButton = true;
  disableNextButton = false;
  slideLoaded = true;
  isEditable = false;
  clientsAndResults: Array<any> = [];
  groupedROIChartOptions: any;
  groupedApiROIChartOptions: any;
  roiCategories: Array<any> = [];
  year1RoiCategories: Array<any> = [];
  year2RoiCategories: Array<any> = [];
  graphPayload: any;
  graphBackendPayload: any;
  pptCustomizations: any;
  currentYear: any;
  taxFreeIncome: number;
  totalPlanAmount: number;
  taxSavings: number;
  year1PlanFee: number;
  option1Payus: number;
  option2PayIRS: number;
  option1Keep: number;
  planAmount: number;
  editing: any = {};
  option2Keep: number;
  netSavings: number;
  whatYouKeep: any = {};
  year1Total: number;
  actualYear1PlanFee: number;
  option1PayIRS: number;
  actualTaxFreeIncome: number;
  option2PayUs: number;
  orgCSS: any = ORG_LOGO_CSS;
  Highcharts: typeof Highcharts = Highcharts;

  constructor(
    private loadingService: LoadingService,
    private toastService: ToastService,
    private switchBoard: SwitchBoard,
    private logoService: LogoService,
    private taxPlanningService: TaxPlanningService,
    private numeralService: NumeralService
  ) {}

  ngOnInit(): void {
    const base = this;
    this.user = Session.getUser();
    this.orgName = this.user.org || '';
    this.orgLogo = this.logoService.getLoginLogoUrl();
    this.backgroundColor = '#fff';
    this.logoBg = {
      background: 'url("' + this.orgLogo + '") center center no-repeat',
      'background-size': 'contain'
    };

    const cssArray = this.orgCSS[this.org];
    if (cssArray) {
      _.each(cssArray, function(obj) {
        base.logoBg[obj.style] = obj.value;
      });
    }
    this.currentEnvironment = environment;
    this.currentYear = new Date().getFullYear();
    this.getRequiredData();
    this.calculateTaxFreeIncome();
    this.hasROIGraphData = true;
    this.switchBoard.onToastConfirm.pipe(takeUntil(this.destroy$)).subscribe(toast => {
      if (toast.data){
      }
    });
  }

  getRequiredData() {
    this.loadingService.triggerLoadingEvent(true);
    const base = this;
    const services = [];
    let year = _.cloneDeep(this.planYear);
    year = parseInt(year) + 1;
    this.planSecondYear = year;
    services.push(this.taxPlanningService.getTaxPlanningSummary(this.org, this.user.id, this.clientTaxplanData.id, 'Category', this.planYear,"", true));
    services.push(this.taxPlanningService.getTaxPlanningSummary(this.org, this.user.id, this.clientTaxplanData.id, 'Category', year,"", true));
    services.push(this.taxPlanningService.getStrategyROIGraphData(this.org, this.user.id, this.clientTaxplanData.id, this.planYear));
    services.push(this.taxPlanningService.getCustomPresentationData(this.org, this.user.id, this.clientTaxplanData.id));
    forkJoin(...services)
      .subscribe(results => {
        this.loadingService.triggerLoadingEvent(false);
        if (results[0]) {
          this.formatRoiSummaryForFirstYear(results[0]);
        }
        if (results[1]) {
          this.formatRoiSummaryForSecondYear(results[1]);
        }
        if (results[2]) {
          this.formatROIGraphData(results[2]);
          this.formatApiROIGraphData(results[2]);
        }
        if (results[3]) {
          this.formatCustomPresentationData(results[3]);
        }
      });
  }

  formatRoiSummaryForFirstYear(responseData) {
    this.roiYear1Data = responseData;
    const data = _.cloneDeep(this.roiYear1Data.categories);
    this.collectCategoriesList(data, 'year1');
    this.year1Total = responseData.currentYearAmount || 0;
    this.option2PayUs = responseData.costLineTotal || 0;
    this.actualYear1PlanFee = responseData.costLineTotal || 0;
    this.option1PayIRS = responseData.currentYearAmount || 0;
    this.option2Keep = (this.option1PayIRS - this.option2PayUs) || 0;
    this.netSavings = (this.option2Keep + this.taxFreeIncome);

    //calclating values
    this.option2PayIRS = this.option1PayIRS - this.year1Total;
    this.option2Keep = this.year1Total - this.option2PayUs;
  }

  formatRoiSummaryForSecondYear(responseData) {
    this.roiYear2Data = responseData;
    const data = _.cloneDeep(this.roiYear2Data.categories);
    this.collectCategoriesList(data, 'year2');
  }

  formatROIGraphData(responseData) {
    const base = this;
    this.roiGraphData = responseData || {};
    this.hasROIGraphData = true;
    this.groupedROIChartOptions = {
      colors: this.chartColors,
      chart: {
        type: 'areaspline',
        style: {
          fontFamily: 'Raleway-Regular',
          fontWeight: '400'
        }
      },
      title: {
        text: 'Projected Net Tax Savings',
        align: 'left',
        style: {
          color: '#444',
          fontFamily: 'Raleway-Regular',
          fontWeight: '300',
          fontSize: '24'
        }
      },
      legend: {},
      xAxis: {
        categories: base.roiGraphData.categories
      },
      yAxis: {
        title: {
          text: ''
        },
        labels: {
          formatter: function() {
            if (this.value >= 1E6) {
              return '$' + (this.value / 1000000).toFixed(2) + 'M';
            }
            return '$' + this.value / 1000 + 'k';
          }
        }
      },
      credits: {
        enabled: false
      },
      plotOptions: {
        areaspline: {
          fillOpacity: 0.5,
          dataLabels: {
            enabled: true,
            formatter(){
              return base.formatAmount(this.y);
            }
          },
          enableMouseTracking: false
        }

      },
      series: [{
        name: 'ROI',
        data: base.roiGraphData.data
      }]
    };
    this.graphPayload = {
      options: JSON.stringify(this.groupedROIChartOptions),
      filename: "dashboard",
      type: 'image/png',
      b64: true
    };
  }

  // Send Graph data to API
formatApiROIGraphData(responseData) {
  const base = this;
  this.roiGraphData = responseData || {};
  this.hasROIGraphData = true;

  this.groupedApiROIChartOptions = {
    colors: this.chartColors,
    chart: {
      type: 'areaspline',
      style: {
        fontFamily: 'Raleway-Regular',
        fontWeight: '400'
      }
    },
    title: {
      text: 'Projected Net Tax Savings',
      align: 'left',
      style: {
        color: '#444',
        fontFamily: 'Raleway-Regular',
        fontWeight: '300',
        fontSize: '24'
      }
    },
    legend: {},
    xAxis: {
      categories: base.roiGraphData.categories || []
    },
    yAxis: {
      title: { text: '' },
      labels: {
        format: '${value:,.0f}'   // ✅ Matches your static JSON
      }
    },
    credits: { enabled: false },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.5,
        dataLabels: {
          enabled: true,
          format: '${y:,.0f}'    // ✅ Matches your static JSON
        },
        enableMouseTracking: false
      }
    },
    series: [{
      name: 'ROI',
      data: base.roiGraphData.data || []
    }]
  };

  // Final API payload
  this.graphBackendPayload = {
    options: this.groupedApiROIChartOptions,
    filename: "dashboard",
    type: 'image/png',
    b64: true
  };
}

  formatCustomPresentationData(responseData) {
    this.pptCustomizations = responseData;
    if (responseData && responseData.length > 0) {
      _.each(responseData, (item) => {
        if (item.slideName === 'clients_results') {
          this.clientsAndResults = JSON.parse(item.customization).clients_results || [];
        }
      });
    } else {
      this.clientsAndResults = [
        {clientName: 'Client #1-', investment: '23,725', projectedSavings: '43,001', actualSavings: '56,222'},
        {clientName: 'Client #2-', investment: '9,400', projectedSavings: '24,906', actualSavings: '29,245'},
        {clientName: 'Client #3-', investment: '42,815', projectedSavings: '173,095', actualSavings: '311,939'},
      ];

      //calclating values
      this.option2PayIRS = this.option1PayIRS - this.year1Total;
      this.option2Keep = this.year1Total - this.option2PayUs;
    }
  }

  calculateTaxFreeIncome(){
    this.taxFreeIncome = 0;
    this.actualTaxFreeIncome = 0;
    if (this.clientTaxplanData.strategies) {
      _.each(this.clientTaxplanData.strategies, (strategy) => {
        if (!isNaN(strategy.amount)) {
          if (strategy.taxFreeIncome) {
            this.taxFreeIncome += strategy.amount;
            this.actualTaxFreeIncome += strategy.amount;
          }
        }
      });
    }
  }

  enableEditing(key){
    this.editing[key] = true;
  }

  closeEditing(key){
    this.editing[key] = false;
    if (key === 'option1PayIRS') {
      this.option2PayIRS = this.option1PayIRS - this.year1Total;
      this.option2Keep = this.year1Total - this.option2PayUs;
    }
    this.savePresentationCustomValues();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.switchBoard.onToggleFeedback.next({
      display: false
    });
  }

  formatAmount(amount){
    return this.numeralService.format('$0,0', amount);
  }

  showPreviousSlide() {
    const slideNum = parseInt(this.currentSlide.split('-')[1]);
    let prevSlideNum;
    if (slideNum && slideNum !== 1) {
      prevSlideNum = slideNum - 1;
      this.disablePrevButton = prevSlideNum === 1;
      this.disableNextButton = false;
      this.slideLoaded = false;
      setTimeout(() => {
        this.currentSlide = 'slide-' + prevSlideNum;
        this.slideLoaded = true;
      }, 50);
    } else {
      this.disablePrevButton = true;
    }
  }

  showNextSlide() {
    const slideNum = parseInt(this.currentSlide.split('-')[1]);
    let nextSlideNum;
    if (slideNum && (slideNum !== this.slides.length)) {
      nextSlideNum = slideNum + 1;
      this.disableNextButton = (nextSlideNum === this.slides.length);
      this.disablePrevButton = false;
      this.slideLoaded = false;
      setTimeout(() => {
        this.currentSlide = 'slide-' + nextSlideNum;
        this.slideLoaded = true;
      }, 50);
    } else {
      this.disableNextButton = true;
    }
  }

  toggleEdit() {
    this.isEditable = !this.isEditable;
  }

  appendClient() {
    if (this.clientsAndResults.length < 5) {
      this.clientsAndResults.push({clientName: '', investment: '', projectedSavings: '', actualSavings: ''});
    } else {
      this.toastService.pop(TOAST_TYPE.warning, 'Limit reached.');
    }
  }

  collectCategoriesList(data, type) {
    let catArray = [];
    if (data && data.length > 0) {
      _.each(data, (item) => {
        if (item.strategyInfo && item.strategyInfo.length > 0) {
          catArray = _.unionBy(catArray, item.strategyInfo, 'name');
        }
      });
      if (type === 'year1') {
        this.year1RoiCategories = catArray;
      } else if (type === 'year2') {
        this.year2RoiCategories = catArray;
      }
    }
  }

  getFormatedGraphData(graphData) {
    const base = this;
    const data = [];
    if (graphData && graphData.length > 0) {
      _.each(graphData, (item) => {
        item = base.formatAmount(item);
        data.push(item);
      });
    }
    return data;
  }

  exportToPPT(){
    const pptRequest = {
      clientName: `${this.clientData.firstName} ${this.clientData.lastName}`.trim(),
      fileName: `ROI - ${this.clientData.lastName}.pptx`,
      firmLogo: this.orgLogo,
      orgName: this.orgName,
      clientTaxTable: [],
      customizations: this.getFormatedCustomizations(),
      dashboardPayload: this.graphBackendPayload,
      integratedStrategies: {
        categories: this.roiCategories,
        year1TaxSavingsPotential: this.formatAmount(this.roiYear1Data.year1ProjectedGain),
        roiAfter10Years: this.formatAmount(this.roiYear1Data.totalTaxSavings),
        immediateROI: this.roiYear1Data.year1ROI,
        year2: this.roiYear2Data.year1ROI
      },
      year1ROICategories: {
        year: this.planYear,
        planName: this.roiYear1Data.strategyName || '',
        lines: this.roiYear1Data.categories.length > 0 ? ((this.roiYear1Data.categories[0].strategyInfo) ? this.getFormatedLinesData(this.roiYear1Data.categories[0].strategyInfo) : []) : [],
        total: this.roiYear1Data.categories.length > 0 ? this.formatAmount(this.roiYear1Data.categories[0].totalInfo.amount) : 0,
        year1ProjectedGain: this.formatAmount(this.roiYear1Data.year1ProjectedGain),
        year1ROI: this.roiYear1Data.year1ROI,
        disclaimer: this.roiYear1Data.disclaimer || '',
        totalTaxSavings: this.formatAmount(this.roiYear1Data.totalTaxSavings)
      },
      year2ROICategories: {
        year: this.planSecondYear,
        planName: this.roiYear2Data.strategyName || '',
        lines: this.roiYear2Data.categories.length > 0 ? ((this.roiYear2Data.categories[0].strategyInfo) ? this.getFormatedLinesData(this.roiYear2Data.categories[0].strategyInfo) : []) : [],
        total: this.roiYear2Data.categories.length > 0 ? this.formatAmount(this.roiYear2Data.categories[0].totalInfo.amount) : 0,
        year2ProjectedGain: this.formatAmount(this.roiYear2Data.year1ProjectedGain),
        projectedNetTaxSavings: this.formatAmount(this.roiYear2Data.projectedSavingsNext10),
        roi: this.roiYear2Data.year1ROI,
        year10Savings: this.formatAmount(this.roiYear2Data.totalTaxSavings)
      },
      year1CostData: {
        costLines: (this.roiYear1Data.costlines) ? this.getFormatedLinesData(this.roiYear1Data.costlines) : [],
        total: this.formatAmount(this.roiYear1Data.costLineTotal),
        afterTaxInvestment: this.formatAmount(this.roiYear1Data.afterTaxInvestment),
        afterTaxYearlyInvestment: this.formatAmount(this.roiYear1Data.afterTaxYearlyInvestment),
        projectedGainAfterY1: this.formatAmount(this.roiYear1Data.year1ProjectedGain),
        roi: this.roiYear1Data.year1ROI
      },
      year2CostData: {
        costLines: (this.roiYear2Data.costlines) ? this.getFormatedLinesData(this.roiYear2Data.costlines) : [],
        total: this.formatAmount(this.roiYear2Data.costLineTotal),
        afterTaxInvestment: this.formatAmount(this.roiYear2Data.afterTaxInvestment),
        afterTaxYearlyInvestment: this.formatAmount(this.roiYear2Data.afterTaxYearlyInvestment),
        projectedGainAfterY2: this.formatAmount(this.roiYear2Data.projectedSavingsNext10),
        roi: this.roiYear2Data.year1ROI,
        totalTaxSavings: this.formatAmount(this.roiYear2Data.totalTaxSavings)
      },
      whatYouKeep: {
        year1Total: this.numeralService.format('$0,0', this.year1Total),
        actualTaxFreeIncome: this.numeralService.format('$0,0', this.actualTaxFreeIncome),
        totalAmount: this.numeralService.format('$0,0', (this.year1Total + this.actualTaxFreeIncome)),
        actualYear1PlanFee: this.numeralService.format('$0,0', this.actualYear1PlanFee),
        whatYouKeep: this.numeralService.format('$0,0', this.year1Total + this.actualTaxFreeIncome - this.actualYear1PlanFee),
        totalSavings: this.numeralService.format('$0,0', this.roiYear1Data.totalTaxSavings)
      },
      optionsData: {
        option1PayUs: this.numeralService.format('$0,0', this.option1Payus),
        option1PayIRS: this.numeralService.format('$0,0', this.option1PayIRS),
        option1Keep: this.numeralService.format('$0,0', this.option1Keep),
        option2PayUs: this.numeralService.format('$0,0', this.option2PayUs),
        option2PayIRS: this.numeralService.format('$0,0', this.option2PayIRS),
        option2Keep: this.numeralService.format('$0,0', this.option2Keep),
        taxFreeIncome: this.numeralService.format('$0,0', this.taxFreeIncome),
        netSavings: this.numeralService.format('$0,0', this.netSavings)
      }
    };

    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getPPTDownloadLink(this.org, this.user.id, pptRequest).subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      window.open(response.fileUrl, '_blank');
      this.taxPlanningService.logReportDownload(this.org, this.user.id, {
        taxPlan: this.clientTaxplanData,
        format: 'ppt',
        status: 'success',
      }).subscribe();
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, 'Failed To Download');
      this.taxPlanningService.logReportDownload(this.org, this.user.id, {
        taxPlan: this.clientTaxplanData,
        format: 'ppt',
        status: 'failure',
        errorMessage: error?.message || 'Unknown error exporting presentation ppt',
      }).subscribe();
    });
  }

  getFormatedLinesData(linesData) {
    const base = this;
    const data = _.cloneDeep(linesData);
    if (data && data.length > 0) {
      _.each(data, (item) => {
        if (item && item.amount) {
          item.amount = base.formatAmount(item.amount);
        }
      });
    }
    return data;
  }

  savePresentationCustomValues() {
    this.isEditable = false;
    const obj = {clients_results: this.clientsAndResults, whatYouKeep: this.whatYouKeep};
    const data = {
      planId: this.clientTaxplanData.id,
      slideName: 'clients_results',
      customization: JSON.stringify(obj)
    };

    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.saveCustomPresentationData(this.org, this.user.id, data)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.success, 'Saved Successfully');
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        try {
          const message = error.message || 'Failed To save ppt customizations';
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (err) {
          this.toastService.pop(TOAST_TYPE.error, 'Failed To save ppt customizations');
        }
      });
  }

  deleteClientsResults(index) {
    this.clientsAndResults.splice(index, 1);
  }

  getFormatedCustomizations() {
    const base = this;
    let data = _.cloneDeep(this.pptCustomizations);
    if (data && data.length > 0) {
      if (data[0].slideName === 'clients_results') {
        const results = _.cloneDeep(this.clientsAndResults);
        if (results && results.length > 0) {
          _.each(results, (item) => {
            if (item) {
              item.investment = base.formatAmount(item.investment);
              item.projectedSavings = base.formatAmount(item.projectedSavings);
              item.actualSavings = base.formatAmount(item.actualSavings);
            }
          });
        }
        data[0].customization = JSON.stringify({clients_results: results});
      }
    } else {
      data = [];
      const results = [
        {clientName: 'Client #1-', investment: '23,725', projectedSavings: '43,001', actualSavings: '56,222'},
        {clientName: 'Client #2-', investment: '9,400', projectedSavings: '24,906', actualSavings: '29,245'},
        {clientName: 'Client #3-', investment: '42,815', projectedSavings: '173,095', actualSavings: '311,939'},
      ];
      _.each(results, (item) => {
        if (item) {
          item.investment = base.formatAmount(item.investment);
          item.projectedSavings = base.formatAmount(item.projectedSavings);
          item.actualSavings = base.formatAmount(item.actualSavings);
        }
      });
      data.push({
        planId: this.clientTaxplanData.id,
        slideName: 'clients_results',
        customization: JSON.stringify({clients_results: results})
      });
    }
    return data;
  }

}
